import { useState, useEffect, useCallback, useMemo } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { PLATFORM_PERMISSIONS } from '../../../../../../backend/src/shared/action-registry';
import {
  ADMINISTRATOR_ROLE_NAME,
  SEAT_BASED_ROLE_NAME,
  SYSTEM_ROLE_NAMES as BACKEND_SYSTEM_ROLE_NAMES,
} from '../../../../../../backend/src/user/roles/user-roles-defaults';
import { useCreateRole } from '@/api/mutations/useCreateRole';
import { useDeleteRole } from '@/api/mutations/useDeleteRole';
import { useSetDefaultRole } from '@/api/mutations/useSetDefaultRole';
import { useUpdateRole } from '@/api/mutations/useUpdateRole';
import { useGetRoles, Role } from '@/api/queries/useGetRoles';
import { PERMISSION_GROUPS, type PermissionInfo } from '@/constants/permissionGroups';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { useHasPermission } from '@/hooks/useHasPermission';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { EditFieldDialogControlled } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { SettingsContent, SettingsRow, SettingsSection } from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

// ===== Types =====

interface EditedRole {
  name: string;
  permissions: string[];
}

// ===== Constants =====

const SYSTEM_ROLE_NAMES = new Set<string>(BACKEND_SYSTEM_ROLE_NAMES);

const isSystemRole = (role: Role) => SYSTEM_ROLE_NAMES.has(role.name);

const sortRoles = (roles: Role[]) =>
  [...roles]
    // Hide Seat-based role unless it has users assigned (only relevant for seat-based orgs)
    .filter((role) => role.name !== SEAT_BASED_ROLE_NAME || role.userCount > 0)
    .sort((a, b) => {
      const aSystem = isSystemRole(a);
      const bSystem = isSystemRole(b);
      if (aSystem && !bSystem) return -1;
      if (!aSystem && bSystem) return 1;
      // Administrator always first among system roles
      if (a.name === ADMINISTRATOR_ROLE_NAME) return -1;
      if (b.name === ADMINISTRATOR_ROLE_NAME) return 1;
      return a.name.localeCompare(b.name);
    });

const VALID_PERMISSIONS = new Set(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.filter((p) => !p.delegatedBy).map((p) => p.permission)),
);

const COL_LEFT = 280;

const getChangeCount = (role: Role, edited?: EditedRole) => {
  if (!edited) return 0;

  const originalPermissions = new Set(role.permissions);
  const editedPermissions = new Set(edited.permissions);
  const permissionChanges =
    [...originalPermissions].filter((permission) => !editedPermissions.has(permission)).length +
    [...editedPermissions].filter((permission) => !originalPermissions.has(permission)).length;

  return permissionChanges + (edited.name !== role.name ? 1 : 0);
};

// ===== Roles overview =====

export const Roles = ({
  organisationId,
  permissionsPath = '/organisation-settings/roles/permissions',
}: { organisationId?: string; permissionsPath?: string } = {}) => {
  const { data: rolesData } = useGetRoles(organisationId);
  const roles = useMemo(() => rolesData ?? [], [rolesData]);
  const { hasPermission } = useHasPermission();
  const canManagePlatformPermissions = hasPermission(Permission.PlatformManagePermissions);
  const { mutate: setDefaultRole, isLoading: isSettingDefaultRole } = useSetDefaultRole(organisationId);
  const navigate = useNavigate();

  const defaultRoleId = useMemo(() => roles.find((role) => role.isDefault)?.id ?? '', [roles]);
  const defaultRoleOptions = useMemo(
    () =>
      sortRoles(roles)
        .filter(
          (role) =>
            role.isDefault ||
            canManagePlatformPermissions ||
            !role.permissions.some((permission) => PLATFORM_PERMISSIONS.has(permission as Permission)),
        )
        .map((role) => ({ label: role.name, value: role.id })),
    [canManagePlatformPermissions, roles],
  );

  const handleDefaultRoleChange = (roleId: string) => {
    if (roleId && roleId !== defaultRoleId) {
      setDefaultRole(roleId);
    }
  };

  return (
    <SettingsContent>
      <SettingsSection
        title="Roles and permissions"
        description="Manage the default role and configure role permissions for this organisation."
      >
        <PermissionGate permission={Permission.UserManage} uiMode="disabled">
          <SettingsRow
            title="Default role"
            description="The role assigned to users when they are added to the organisation."
          >
            <SearchableCombobox
              options={defaultRoleOptions}
              value={defaultRoleId}
              onChange={handleDefaultRoleChange}
              placeholder="Select a role"
              searchPlaceholder="Search roles..."
              emptyText="No roles found"
              disabled={isSettingDefaultRole}
              clearable={false}
              triggerClassName="w-56 justify-between font-normal"
            />
          </SettingsRow>
        </PermissionGate>

        <SettingsRow
          title="Permissions configurator"
          description="Create roles and configure the permissions granted to each role."
          className="cursor-pointer transition-colors hover:bg-gray-50"
          onClick={() => navigate(permissionsPath)}
        >
          <Icon className="lucide--chevron-right mr-2 text-gray-400" />
        </SettingsRow>
      </SettingsSection>
    </SettingsContent>
  );
};

// ===== Permissions configurator =====

export const Permissions = ({
  organisationId,
  description = 'Create roles and configure the permissions granted to each role.',
}: {
  organisationId?: string;
  description?: string;
} = {}) => {
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { data: rolesData } = useGetRoles(organisationId);
  const roles = useMemo(() => rolesData ?? [], [rolesData]);
  const { mutate: createRole, mutateAsync: createRoleAsync, isLoading: isCreating } = useCreateRole(organisationId);
  const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole(organisationId);
  const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole(organisationId);
  // Track edits per role: roleId -> { name, permissions }
  const [editedRoles, setEditedRoles] = useState<Record<string, EditedRole>>({});
  const [editingNameRoleId, setEditingNameRoleId] = useState<string | null>(null);
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [deleteModalRoleId, setDeleteModalRoleId] = useState<string | null>(null);
  const visiblePermissionGroups = useMemo(
    () => PERMISSION_GROUPS.filter((group) => group.scope !== 'platform' || user?.organisation?.isGlobalAdmin === true),
    [user?.organisation?.isGlobalAdmin],
  );
  const canManagePlatformPermissions = hasPermission(Permission.PlatformManagePermissions);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(PERMISSION_GROUPS.map((group) => group.name)),
  );

  // Initialize edited state when roles load or change
  useEffect(() => {
    setEditedRoles((prev) => {
      const next: Record<string, EditedRole> = {};
      for (const role of roles) {
        // Keep existing edits if the role hasn't changed server-side
        const existing = prev[role.id];
        if (existing) {
          next[role.id] = existing;
        } else {
          next[role.id] = { name: role.name, permissions: [...role.permissions] };
        }
      }
      return next;
    });
  }, [roles]);

  // Server permissions for highlighting new selections
  const originalRolePermissions = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    for (const role of roles) {
      map[role.id] = new Set(role.permissions);
    }
    return map;
  }, [roles]);

  const sortedRoles = useMemo(() => sortRoles(roles), [roles]);

  const isDirty = useCallback((role: Role) => getChangeCount(role, editedRoles[role.id]) > 0, [editedRoles]);

  const dirtyRoles = useMemo(() => sortedRoles.filter((r) => isDirty(r) && !isSystemRole(r)), [sortedRoles, isDirty]);
  const unsavedChangeCount = useMemo(
    () => dirtyRoles.reduce((count, role) => count + getChangeCount(role, editedRoles[role.id]), 0),
    [dirtyRoles, editedRoles],
  );

  const handlePermissionToggle = (roleId: string, permission: string) => {
    setEditedRoles((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      const has = current.permissions.includes(permission);
      return {
        ...prev,
        [roleId]: {
          ...current,
          permissions: has ? current.permissions.filter((p) => p !== permission) : [...current.permissions, permission],
        },
      };
    });
  };

  const handleGroupToggle = (roleId: string, groupPermissions: string[]) => {
    setEditedRoles((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      const allSelected = groupPermissions.every((p) => current.permissions.includes(p));
      let newPermissions: string[];
      if (allSelected) {
        newPermissions = current.permissions.filter((p) => !groupPermissions.includes(p));
      } else {
        newPermissions = [...current.permissions];
        for (const p of groupPermissions) {
          if (!newPermissions.includes(p)) {
            newPermissions.push(p);
          }
        }
      }
      return { ...prev, [roleId]: { ...current, permissions: newPermissions } };
    });
  };

  const handleNameChange = (roleId: string, name: string) => {
    setEditedRoles((prev) => {
      const current = prev[roleId];
      if (!current) return prev;
      return { ...prev, [roleId]: { ...current, name } };
    });
  };

  const isNameInUse = useCallback(
    (name: string, excludedRoleId?: string) => {
      const trimmed = name.trim().toLowerCase();
      if (!trimmed) return false;
      return sortedRoles.some((r) => {
        if (r.id === excludedRoleId) return false;
        const savedName = r.name.trim().toLowerCase();
        const editedName = editedRoles[r.id]?.name.trim().toLowerCase();
        return savedName === trimmed || editedName === trimmed;
      });
    },
    [sortedRoles, editedRoles],
  );

  const handleSaveAll = () => {
    for (const role of dirtyRoles) {
      const edited = editedRoles[role.id];
      if (!edited || !edited.name.trim() || edited.name.length > 50) continue;
      updateRole({ roleId: role.id, name: edited.name.trim(), permissions: edited.permissions });
    }
  };

  const handleDiscardAll = () => {
    setEditedRoles((prev) => {
      const next = { ...prev };
      for (const role of dirtyRoles) {
        next[role.id] = { name: role.name, permissions: [...role.permissions] };
      }
      return next;
    });
  };

  const getUniqueName = useCallback(
    (base: string) => {
      const names = new Set(roles.map((r) => r.name.trim().toLowerCase()));
      if (!names.has(base.trim().toLowerCase())) return base;
      let n = 1;
      while (names.has(`${base} (${n})`.toLowerCase())) n++;
      return `${base} (${n})`;
    },
    [roles],
  );

  const handleCreateRole = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Enter a role name');
      throw new Error('Role name is required');
    }
    if (isNameInUse(trimmed)) {
      toast.error('A role with this name already exists');
      throw new Error('Role name already exists');
    }
    await createRoleAsync({ name: trimmed, permissions: [] });
  };

  const handleDuplicate = (role: Role) => {
    const permissions = [...role.permissions].filter(
      (permission) =>
        (VALID_PERMISSIONS as Set<string>).has(permission) &&
        (canManagePlatformPermissions || !PLATFORM_PERMISSIONS.has(permission as Permission)),
    );
    createRole(
      { name: getUniqueName(`${role.name} (duplicate)`), permissions },
      { onSuccess: (newRole) => setEditingNameRoleId(newRole.id) },
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteModalRoleId) return;
    deleteRole(deleteModalRoleId, {
      onSuccess: () => setDeleteModalRoleId(null),
    });
  };

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const deleteModalRole = roles.find((r) => r.id === deleteModalRoleId);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="mb-6 flex items-center justify-between gap-4 px-1">
        <div>
          <h2 className="text-xl font-medium text-gray-800">Permissions Configuration</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">{description}</p>
        </div>
        <Button variant="primary" onClick={() => setIsCreateRoleOpen(true)} disabled={isCreating}>
          <Icon className="lucide--plus" />
          Create new role
        </Button>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* Single scrollable container — extra bottom padding so last rows clear the floating save bar */}
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="border-collapse">
            <colgroup>
              <col style={{ width: COL_LEFT }} />
              {sortedRoles.map((role) => (
                <col key={role.id} />
              ))}
            </colgroup>

            <thead>
              <tr className="sticky top-0 z-20">
                {/* Corner: Permission header — sticky both directions */}
                <th className="sticky left-0 z-30 border-b border-gray-200 bg-white px-4 py-2.5 text-left align-bottom">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Permission</span>
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              const allGroupNames = visiblePermissionGroups.map((g) => g.name);
                              const allCollapsed = allGroupNames.every((n) => collapsedGroups.has(n));
                              setCollapsedGroups(allCollapsed ? new Set() : new Set(allGroupNames));
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Icon
                              className={`h-3.5 w-3.5 ${
                                visiblePermissionGroups.every((g) => collapsedGroups.has(g.name))
                                  ? 'lucide--chevrons-down-up'
                                  : 'lucide--chevrons-up-down'
                              }`}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {visiblePermissionGroups.every((g) => collapsedGroups.has(g.name))
                            ? 'Expand all'
                            : 'Collapse all'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </th>

                {/* Role headers */}
                {sortedRoles.map((role) => (
                  <RoleColumnHeader
                    key={role.id}
                    role={role}
                    editedName={editedRoles[role.id]?.name ?? role.name}
                    isEditingName={editingNameRoleId === role.id}
                    isSystem={isSystemRole(role)}
                    isUpdating={isUpdating}
                    hasPlatformPermissions={
                      user?.organisation?.isGlobalAdmin === true &&
                      (editedRoles[role.id]?.permissions ?? role.permissions).some((permission) =>
                        PLATFORM_PERMISSIONS.has(permission as Permission),
                      )
                    }
                    onStartEditingName={() => setEditingNameRoleId(role.id)}
                    onStopEditingName={() => setEditingNameRoleId(null)}
                    onNameChange={(name) => handleNameChange(role.id, name)}
                    isNameInUse={(name) => isNameInUse(name, role.id)}
                    onDuplicate={() => handleDuplicate(role)}
                    onDelete={() => setDeleteModalRoleId(role.id)}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {visiblePermissionGroups.map((group) => {
                const groupPermissions = group.permissions.map((p) => p.permission);
                return (
                  <PermissionGroupRows
                    key={group.name}
                    group={group}
                    groupPermissions={groupPermissions}
                    isCollapsed={collapsedGroups.has(group.name)}
                    onToggleCollapse={() => toggleGroup(group.name)}
                    sortedRoles={sortedRoles}
                    editedRoles={editedRoles}
                    originalRolePermissions={originalRolePermissions}
                    onPermissionToggle={handlePermissionToggle}
                    onGroupToggle={handleGroupToggle}
                    isUpdating={isUpdating}
                    canEdit={group.scope !== 'platform' || canManagePlatformPermissions}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Floating save bar — overlays the bottom of the table while there are changes */}
        <AnimatePresence>
          {unsavedChangeCount > 0 && (
            <motion.div
              className="absolute right-4 bottom-4 left-4 z-40"
              initial={{ y: 4, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 4, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center justify-end gap-5 rounded-lg border bg-white p-4 shadow-lg/2">
                <div className="flex items-center gap-2">
                  <Icon className="lucide--alert-circle text-amber-500" />
                  <span className="text-sm text-gray-600">
                    {unsavedChangeCount} unsaved change{unsavedChangeCount !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleDiscardAll} disabled={isUpdating}>
                    Discard all changes
                  </Button>
                  <Button onClick={handleSaveAll} disabled={isUpdating} variant="primary">
                    Save all changes
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <EditFieldDialogControlled
        open={isCreateRoleOpen}
        onOpenChange={setIsCreateRoleOpen}
        title="Create role"
        description="Enter a name for this role. You can configure its permissions after creating it."
        fieldLabel="Name"
        currentValue=""
        placeholder="Role name"
        saveLabel="Create role"
        savingLabel="Creating..."
        onSave={handleCreateRole}
      />
      {deleteModalRole && (
        <ConfirmationModal
          isOpen={Boolean(deleteModalRoleId)}
          onOpenChange={(open) => {
            if (!open) setDeleteModalRoleId(null);
          }}
          title="Delete Role"
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
        >
          <p>
            Are you sure you want to delete the role &quot;{deleteModalRole.name}&quot;? This action cannot be undone.
          </p>
        </ConfirmationModal>
      )}
    </div>
  );
};

// ===== Role column header =====

interface RoleColumnHeaderProps {
  role: Role;
  editedName: string;
  isEditingName: boolean;
  isSystem: boolean;
  isUpdating: boolean;
  hasPlatformPermissions: boolean;
  onStartEditingName: () => void;
  onStopEditingName: () => void;
  onNameChange: (name: string) => void;
  isNameInUse: (name: string) => boolean;
  onDuplicate: () => void;
  onDelete: () => void;
}

const RoleColumnHeader = ({
  role,
  editedName,
  isEditingName,
  isSystem,
  hasPlatformPermissions,
  onStartEditingName,
  onStopEditingName,
  onNameChange,
  isNameInUse,
  onDuplicate,
  onDelete,
}: RoleColumnHeaderProps) => {
  const canDelete = !isSystem && !role.isDefault && role.userCount === 0;

  const getDeleteTooltip = () => {
    if (role.isDefault) return 'Default role cannot be deleted';
    if (role.userCount > 0) return "Roles can't be deleted while assigned to users";
    return '';
  };

  return (
    <th className="min-w-[128px] border-b border-l border-gray-200 bg-white px-4 py-2 text-left align-bottom">
      <div className="flex flex-col items-start gap-1">
        {/* User count + badges */}
        <div className="flex items-center gap-1.5 font-normal">
          <span className="text-xs whitespace-nowrap text-gray-500">
            {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
          </span>
          {isSystem && (
            <span className="rounded-full border border-teal-200 bg-teal-50 px-1.25 py-0.25 text-[10px] font-normal text-teal-500">
              Wexler
            </span>
          )}
          {hasPlatformPermissions && (
            <span className="rounded-full border border-purple-200 bg-purple-50 px-1.25 py-0.25 text-[10px] font-normal text-purple-500">
              Platform
            </span>
          )}
        </div>

        {/* Name */}
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium whitespace-nowrap text-gray-900">{editedName}</span>
            {role.isDefault && (
              <span className="rounded-full border bg-gray-100 px-1.25 py-0.25 text-[11px] font-normal text-gray-500">
                Default
              </span>
            )}
          </div>
          <DropdownMenu size="sm">
            <DropdownMenuTrigger asChild>
              <button className="text-gray-400 hover:text-gray-600">
                <Icon className="lucide--more-vertical" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="bottom" className="min-w-32">
              {!isSystem && (
                <DropdownMenuItem onClick={onStartEditingName}>
                  <Icon className="lucide--pencil-line" />
                  Rename
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onDuplicate}>
                <Icon className="lucide--copy" />
                Duplicate
              </DropdownMenuItem>
              {!isSystem &&
                (canDelete ? (
                  <DropdownMenuItem onClick={onDelete} className="text-red-600">
                    <Icon className="lucide--trash-2" />
                    Delete
                  </DropdownMenuItem>
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <div>
                          <DropdownMenuItem disabled>
                            <Icon className="lucide--trash-2" />
                            Delete
                          </DropdownMenuItem>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{getDeleteTooltip()}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!isSystem && (
        <EditFieldDialogControlled
          open={isEditingName}
          onOpenChange={(open) => {
            if (!open) onStopEditingName();
          }}
          title="Rename role"
          description="Enter a new name for this role."
          fieldLabel="Name"
          currentValue={editedName}
          placeholder="Role name"
          onSave={(value) => {
            const trimmed = value.trim();
            if (isNameInUse(trimmed)) {
              toast.error('A role with this name already exists');
              throw new Error('Role name already exists');
            }
            onNameChange(trimmed || role.name);
          }}
        />
      )}
    </th>
  );
};

// ===== Permission group rows (merged label + checkbox) =====

interface PermissionGroupRowsProps {
  group: {
    name: string;
    depth: number;
    permissions: {
      permission: string;
      description: string;
      delegatedDescriptions?: string[];
      delegatedBy?: string[];
    }[];
    scope: 'organisation' | 'platform';
  };
  groupPermissions: string[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  sortedRoles: Role[];
  editedRoles: Record<string, EditedRole>;
  originalRolePermissions: Record<string, Set<string>>;
  onPermissionToggle: (roleId: string, permission: string) => void;
  onGroupToggle: (roleId: string, groupPermissions: string[]) => void;
  isUpdating: boolean;
  canEdit: boolean;
}

const PermissionGroupRows = ({
  group,
  isCollapsed,
  onToggleCollapse,
  sortedRoles,
  editedRoles,
  originalRolePermissions,
  onPermissionToggle,
  onGroupToggle,
  isUpdating,
  canEdit,
}: PermissionGroupRowsProps) => {
  // Only non-delegated permissions participate in group-level toggle
  const editableGroupPermissions = useMemo(
    () => group.permissions.filter((p) => !p.delegatedBy).map((p) => p.permission),
    [group.permissions],
  );

  return (
    <>
      {/* Group header row */}
      <tr className="border-t border-gray-200">
        {/* Left: group name — sticky */}
        <td
          className="sticky left-0 z-10 border-r border-gray-200 bg-gray-50 py-2 pr-2"
          style={{ paddingLeft: `${16 + (group.depth - 1) * 20}px` }}
        >
          <button
            onClick={onToggleCollapse}
            className="flex items-center gap-2 text-sm whitespace-nowrap text-gray-900"
          >
            <Icon
              className={`lucide--chevron-right h-3.5 w-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
            />
            {group.name}
            {group.scope === 'platform' && (
              <Tooltip>
                <TooltipTrigger>
                  <Icon className="lucide--shield-user text-purple-500" />
                </TooltipTrigger>
                <TooltipContent>Permissions for organisations able to manage the Wexler platform</TooltipContent>
              </Tooltip>
            )}
          </button>
        </td>

        {/* Right: group-level checkboxes */}
        {sortedRoles.map((role) => {
          const edited = editedRoles[role.id];
          if (!edited) return <td key={role.id} className="border-l border-gray-200 bg-gray-50" />;
          const original = originalRolePermissions[role.id];
          const allSelected = editableGroupPermissions.every((p) => edited.permissions.includes(p));
          const someSelected = !allSelected && editableGroupPermissions.some((p) => edited.permissions.includes(p));
          const system = isSystemRole(role);

          const hasNewInGroup = allSelected && original && editableGroupPermissions.some((p) => !original.has(p));
          return (
            <td key={role.id} className="border-l border-gray-200 bg-gray-50 px-4 py-2">
              <div className="flex justify-center">
                <Checkbox
                  checked={allSelected}
                  ref={(el) => {
                    if (el) {
                      const input = el as unknown as HTMLButtonElement;
                      input.dataset.indeterminate = String(someSelected);
                    }
                  }}
                  onCheckedChange={() => onGroupToggle(role.id, editableGroupPermissions)}
                  disabled={system || isUpdating || !canEdit}
                  className={`h-4 w-4 ${
                    allSelected && hasNewInGroup
                      ? 'data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-500 data-[state=checked]:hover:bg-teal-500/90'
                      : ''
                  }`}
                />
              </div>
            </td>
          );
        })}
        <td className="bg-gray-50" />
      </tr>

      {/* Individual permission rows */}
      {!isCollapsed &&
        group.permissions.map((permissionInfo) => {
          const isDelegated = !!permissionInfo.delegatedBy;
          const parentPermissionLabel = isDelegated ? permissionInfo.delegatedBy!.join(' or ') : null;

          return (
            <tr key={permissionInfo.permission} className="border-t border-gray-100">
              {/* Left: permission label — sticky */}
              <td
                className="sticky left-0 z-10 border-r border-gray-200 bg-white py-1.5 pr-4"
                style={{ paddingLeft: `${39 + (group.depth - 1) * 20}px` }}
              >
                <div className="flex items-center gap-1.5">
                  <span className={`truncate text-sm ${isDelegated ? 'text-gray-400' : 'text-gray-800'}`}>
                    {permissionInfo.permission.split(':')[1]}
                  </span>
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button className="shrink-0">
                          <Icon
                            className={`h-3 w-3 ${isDelegated ? 'lucide--replace text-gray-400' : 'lucide--info text-gray-400'}`}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="text-xs">{permissionInfo.description}</p>
                        {isDelegated && (
                          <p className="mt-1 text-xs text-gray-400">
                            Controlled by the {parentPermissionLabel} permission
                          </p>
                        )}
                        {permissionInfo.delegatedDescriptions && permissionInfo.delegatedDescriptions.length > 0 && (
                          <>
                            <p className="mt-1 text-xs font-medium">Also grants:</p>
                            <ul className="ml-3 list-disc text-xs">
                              {permissionInfo.delegatedDescriptions.map((d) => (
                                <li key={d}>{d}</li>
                              ))}
                            </ul>
                          </>
                        )}
                        {(permissionInfo as PermissionInfo).isOrgScoped && (
                          <p className="mt-1 text-xs text-gray-400">
                            Organisation-level — not affected by Matter-level permission overrides
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </td>

              {/* Right: checkboxes */}
              {sortedRoles.map((role) => {
                const edited = editedRoles[role.id];
                if (!edited) return <td key={role.id} className="border-l border-gray-200 bg-white" />;
                const original = originalRolePermissions[role.id];
                const system = isSystemRole(role);

                if (isDelegated) {
                  // Delegated: checked if any parent permission is active, always disabled
                  const isChecked = permissionInfo.delegatedBy!.some((parent) => edited.permissions.includes(parent));
                  return (
                    <td key={role.id} className="border-l border-gray-200 bg-white px-4 py-1.5">
                      <div className="flex justify-center">
                        <Checkbox checked={isChecked} disabled className="h-4 w-4 opacity-50" />
                      </div>
                    </td>
                  );
                }

                const isChecked = edited.permissions.includes(permissionInfo.permission);
                const isNew = isChecked && original && !original.has(permissionInfo.permission);
                const isRemoved = !isChecked && original && original.has(permissionInfo.permission);

                return (
                  <td key={role.id} className="border-l border-gray-200 bg-white px-4 py-1.5">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => onPermissionToggle(role.id, permissionInfo.permission)}
                        disabled={system || isUpdating || !canEdit}
                        className={`h-4 w-4 ${
                          isNew
                            ? 'data-[state=checked]:border-teal-500 data-[state=checked]:bg-teal-500 data-[state=checked]:hover:bg-teal-500/90'
                            : isRemoved
                              ? 'border-2 border-red-500'
                              : ''
                        }`}
                      />
                    </div>
                  </td>
                );
              })}
              <td className="bg-white" />
            </tr>
          );
        })}
    </>
  );
};
